import React from 'react';
import { IconSun, IconMoon, IconDeviceLaptop } from '@tabler/icons-react';
import { useTheme } from '../../../lib/ThemeProvider';
import { useSlidingIndicator } from '../../../lib/useSlidingIndicator';
import type { ThemePreference } from '../../../lib/theme';

/** The three theme choices, in display order. Auto follows the OS. */
const OPTIONS: { value: ThemePreference; label: string; Icon: typeof IconSun }[] = [
  { value: 'light', label: 'Light', Icon: IconSun },
  { value: 'dark', label: 'Dark', Icon: IconMoon },
  { value: 'auto', label: 'Auto', Icon: IconDeviceLaptop },
];

/**
 * Appearance setting: a Light / Dark / Auto segmented control wired to the
 * app-wide ThemeProvider. The choice persists and takes effect immediately;
 * Auto (the default) tracks the operating system.
 */
export const ThemeSection: React.FC = () => {
  const { preference, setPreference } = useTheme();
  const { containerRef, pillStyle, register } = useSlidingIndicator(preference);
  return (
    <section>
      <h2 className="text-lg font-semibold text-foreground mb-6">Appearance</h2>
      <div
        ref={containerRef}
        role="radiogroup"
        aria-label="Theme"
        // Gap-free track: buttons sit flush so the highlight is always over an
        // item and never bleeds into a gap as it slides.
        className="relative inline-flex p-1 rounded-xl bg-surface border border-border"
      >
        {/* The single highlight that eases between options (see useSlidingIndicator). */}
        <span data-slide-pill aria-hidden="true" className="rounded-lg bg-surface-hover" style={pillStyle} />
        {OPTIONS.map(({ value, label, Icon }) => {
          const active = preference === value;
          return (
            <button
              key={value}
              ref={register(value)}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setPreference(value)}
              className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" aria-hidden="true" />
              {label}
            </button>
          );
        })}
      </div>
    </section>
  );
};
